import { useState, useEffect } from 'react'
import {
  Box,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  CardActions,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Chip,
  Tooltip,
  Snackbar
} from '@mui/material'
import {
  Security as SecurityIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon
} from '@mui/icons-material'
import { getMSPRelationships, revokeMSPAccess, MSPRelationship } from '../services/mspService'

interface MSPRelationshipsListProps {
  tenantId: string
}

export default function MSPRelationshipsList({ tenantId }: MSPRelationshipsListProps) {
  const [relationships, setRelationships] = useState<MSPRelationship[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [revokeDialogOpen, setRevokeDialogOpen] = useState(false)
  const [selectedMSP, setSelectedMSP] = useState<MSPRelationship | null>(null)
  const [revoking, setRevoking] = useState(false)
  const [snackbarOpen, setSnackbarOpen] = useState(false)
  const [snackbarMessage, setSnackbarMessage] = useState('')

  useEffect(() => {
    loadRelationships()
  }, [tenantId])

  const loadRelationships = async () => {
    try {
      setLoading(true)
      setError(null)
      const data = await getMSPRelationships(tenantId)
      // Ensure data is an array
      setRelationships(Array.isArray(data) ? data : [])
    } catch (err: any) {
      console.error('Failed to load MSP relationships:', err)
      setError(err.response?.data?.error || 'Failed to load MSP relationships')
      setRelationships([]) // Set empty array on error
    } finally {
      setLoading(false)
    }
  }

  const handleRevokeClick = (msp: MSPRelationship) => {
    setSelectedMSP(msp)
    setRevokeDialogOpen(true)
  }

  const handleRevokeConfirm = async () => {
    if (!selectedMSP) return

    try {
      setRevoking(true)
      await revokeMSPAccess(tenantId, selectedMSP.mspTenantID)
      setSnackbarMessage(`Successfully revoked access for ${selectedMSP.companyName}`)
      setSnackbarOpen(true)
      setRevokeDialogOpen(false)
      setSelectedMSP(null)
      // Reload relationships
      await loadRelationships()
    } catch (err: any) {
      console.error('Failed to revoke MSP access:', err)
      setError(err.response?.data?.error || 'Failed to revoke MSP access')
    } finally {
      setRevoking(false)
    }
  }

  const handleRevokeCancel = () => {
    setRevokeDialogOpen(false)
    setSelectedMSP(null)
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A'
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    )
  }

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Box display="flex" alignItems="center" gap={1}>
          <SecurityIcon color="primary" />
          <Typography variant="h6">Active MSP Relationships</Typography>
        </Box>
        <Button variant="outlined" size="small" onClick={loadRelationships}>
          Refresh
        </Button>
      </Box>

      {relationships.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            No MSP relationships found. Generate a verification code above to grant MSP access.
          </Typography>
        </Paper>
      ) : (
        <Box display="flex" flexDirection="column" gap={2}>
          {relationships.map((msp) => (
            <Card key={msp.mspTenantID} variant="outlined">
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Typography variant="h6" gutterBottom>
                      {msp.companyName}
                    </Typography>
                    <Box display="flex" gap={1} mb={1}>
                      {msp.isActive ? (
                        <Chip
                          icon={<CheckCircleIcon />}
                          label="Active"
                          color="success"
                          size="small"
                        />
                      ) : (
                        <Chip
                          icon={<WarningIcon />}
                          label="Inactive"
                          color="default"
                          size="small"
                        />
                      )}
                      <Chip
                        label={msp.serviceLevel}
                        color="primary"
                        variant="outlined"
                        size="small"
                      />
                    </Box>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      <strong>Verified:</strong> {formatDate(msp.verifiedAt)}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      <strong>Guest User:</strong> {msp.guestUserEmail || 'Not invited'}
                    </Typography>
                    {msp.invitationSentAt && (
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        <strong>Invitation Sent:</strong> {formatDate(msp.invitationSentAt)}
                      </Typography>
                    )}
                    {msp.invitationAcceptedAt && (
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        <strong>Invitation Accepted:</strong> {formatDate(msp.invitationAcceptedAt)}
                      </Typography>
                    )}
                    <Typography variant="body2" color="text.secondary">
                      <strong>Consent Granted:</strong> {msp.consentGranted ? 'Yes' : 'No'}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
              <CardActions sx={{ justifyContent: 'flex-end', px: 2, pb: 2 }}>
                <Tooltip title="Revoke MSP access and remove guest user">
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={() => handleRevokeClick(msp)}
                    disabled={revoking}
                  >
                    Revoke Access
                  </Button>
                </Tooltip>
              </CardActions>
            </Card>
          ))}
        </Box>
      )}

      {/* Revoke Confirmation Dialog */}
      <Dialog
        open={revokeDialogOpen}
        onClose={handleRevokeCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Revoke MSP Access</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to revoke access for <strong>{selectedMSP?.companyName}</strong>?
          </DialogContentText>
          <Alert severity="warning" sx={{ mt: 2 }}>
            This will:
            <ul style={{ marginTop: 8, marginBottom: 0 }}>
              <li>Remove the MSP's access to your tenant</li>
              <li>Delete the guest user <strong>{selectedMSP?.guestUserEmail}</strong> from your Azure AD</li>
              <li>Prevent the MSP from viewing your deployments and policies</li>
            </ul>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRevokeCancel} disabled={revoking}>
            Cancel
          </Button>
          <Button
            onClick={handleRevokeConfirm}
            color="error"
            variant="contained"
            disabled={revoking}
            startIcon={revoking ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {revoking ? 'Revoking...' : 'Revoke Access'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        message={snackbarMessage}
      />
    </Box>
  )
}
